import { compareDestinationNames, destinationNodeLevels } from '../config'
import type { NearbyDerivationProperty } from '../nearby/centroids'

/**
 * Destination tree derivation — React port of the toolkit's TreeBuilder
 * (homerunner-toolkit includes/DestinationFilter/TreeBuilder.php @ origin/main).
 *
 * The toolkit classifies property_tag terms by configurable name prefixes
 * (island / side / resort) and groups every other level under islands by
 * PROPERTY-SET CO-OCCURRENCE: a term belongs to the island whose property set
 * overlaps its own the most. No manual mapping, no schema, no Central support.
 *
 * WHERE THIS RUNS (#1072 D1): in the dashboard app, on an operator
 * "build/refresh tree" action, NOT in the visitor's browser. The result is
 * stored in widget settings so visitors never pay the derivation cost. This
 * module is therefore pure: it takes an already-fetched property list and
 * returns the tree. It holds no fetch, no cache, and no clock beyond the
 * caller-supplied stamp.
 *
 * PLATFORM MAPPING (verified live against central.homerunner.io feed 46 on
 * 2026-08-26): toolkit property_tag == payload `tags[]`; toolkit property_loc
 * == payload `locations[]`. Both arrive per-property from
 * `/public-api/v1/{feedId}/feed/properties`. Central already strips its own
 * `(loc)`/`[g]` prefixes upstream into derived taxonomies while `tags[]`
 * retains the RAW prefixed names, so prefix classification reads `tags[]`
 * exactly as the PHP reads property_tag. Toolkit property_city == the row's
 * singular `city` ({id,name} or null, central#3137; verified live on prod
 * feed 46 and beta feeds 1/18/107 on 2026-09-28), whose ids are the
 * explorer-meta `taxonomies.cities` ids.
 *
 * KNOWN DIVERGENCES FROM THE PHP, all deliberate:
 *  1. Ordering uses `compareDestinationNames` (localeCompare, sensitivity
 *     'base') rather than the PHP's `strcasecmp`. strcasecmp sorts accented
 *     names after Z, which is the exact bug the Where field's shared
 *     comparator already exists to avoid; re-introducing it here would make
 *     the stored tree disagree with the field rendering it.
 *  2. The PHP leans on `get_terms`' name-ASC ordering for islands. There is no
 *     get_terms here, so islands are sorted explicitly by label — same
 *     resulting order, since every island shares one prefix.
 *  3. City terms get no island. The PHP groups them like cities, but the
 *     toolkit only renders a city-source tab grouped behind its per-tab
 *     `group_by_island` flag, which has no platform setting: By Town renders
 *     flat (tabs.ts), so an island here would have no reader.
 *  4. When no row has a `city` key at all (a payload that predates
 *     central#3137), the explorer-meta terms are kept with count 0 instead of
 *     being dropped as empty, so such a payload builds the tree it built
 *     before (#1623).
 */

export const DESTINATION_TREE_VERSION = 1

export type DestinationLevel = 'island' | 'side' | 'resort'

export type DestinationPrefixes = { island: string; side: string; resort: string }

/** Toolkit fresh-install values (Settings::DEFAULTS['prefixes']). */
export const DEFAULT_DESTINATION_PREFIXES: DestinationPrefixes = {
  island: '(i)',
  side: '(s)',
  resort: '(r)',
}

export type DerivationTerm = { id: number; name: string }

/** The subset of a feed property this derivation reads. */
export type DerivationProperty = {
  id: string
  title: string
  slug?: string | null
  tags?: DerivationTerm[]
  locations?: DerivationTerm[]
  /** Coordinates, as the public feed endpoint already returns them. Unused by
   *  the tree derivation; read by the Nearby index build (#861), which walks
   *  the same fetched pages rather than paging the feed twice. */
  location?: { lat?: number | null; lng?: number | null } | null
  /** The row's property_city relation (central#3137). The tree counts city
   *  terms by its id (#1623); the Nearby build reads it too. Typed as the
   *  Nearby build takes it: both readers gate the id themselves. */
  city?: NearbyDerivationProperty['city']
}

export type DestinationNode = { id: number; label: string; count: number }
export type DestinationIsland = DestinationNode & { sides: DestinationNode[] }
export type DestinationGrouped = DestinationNode & { island: number | null }
export type DestinationHome = { label: string; slug: string }

export type DestinationHygieneRow = {
  label: string
  level: DestinationLevel | 'city' | 'city-term'
  issue: 'empty-label' | 'unusable-id' | 'spans-multiple-islands' | 'no-island-overlap'
  /** Island term ids the term overlapped (only for spans-multiple-islands). */
  islands: number[]
  count: number
}

export type DestinationTree = {
  v: number
  islands: DestinationIsland[]
  /** From per-property `locations[]` (toolkit property_loc). Co-occurrence grouped. */
  cities: DestinationGrouped[]
  /**
   * Toolkit property_city terms, ALWAYS ungrouped with `island: null` (see
   * KNOWN DIVERGENCES 3). When the rows carry a `city` key (central#3137,
   * #1623), these are the city ids the rows carry, counted by property set
   * and labelled from explorer-meta `taxonomies.cities`; a meta term no
   * property carries is dropped. When no row has the key, every meta term is
   * kept with `count: 0`. A stored count of 0 therefore means "built from a
   * pre-#3137 payload", never "no properties": readers must not drop count-0 rows.
   */
  cityTerms: DestinationGrouped[]
  resorts: DestinationGrouped[]
  /** Alphabetised label/slug rows powering the D2 destination search. */
  homes: DestinationHome[]
  hygiene: DestinationHygieneRow[]
  generatedAt: string
}

type TermBucket = { id: number; name: string; ids: Set<string> }
type Classified = { id: number; label: string; level: DestinationLevel; ids: Set<string> }

/** Collect term id -> the set of property ids carrying it. */
function bucketTerms(
  properties: DerivationProperty[],
  pick: (p: DerivationProperty) => DerivationTerm[] | undefined,
): TermBucket[] {
  const byId = new Map<number, TermBucket>()
  for (const property of properties) {
    for (const term of pick(property) ?? []) {
      if (typeof term?.id !== 'number') continue
      let bucket = byId.get(term.id)
      if (!bucket) {
        bucket = { id: term.id, name: String(term.name ?? ''), ids: new Set() }
        byId.set(term.id, bucket)
      }
      bucket.ids.add(property.id)
    }
  }
  // A term only exists here because a property carries it, so the PHP's
  // "drop terms with an empty published set" filter is structural.
  return [...byId.values()].filter((b) => b.ids.size > 0)
}

/**
 * A row's city term id, or null. Numeric strings are coerced (PHP/JSON
 * round-tripping produces them), and isSafeInteger rather than isInteger
 * keeps two ids past 2^53 from collapsing into one — the Nearby build's rule
 * (nearby/centroids.ts).
 */
function cityTermId(raw: unknown): number | null {
  const id = typeof raw === 'string' ? Number(raw) : raw
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0 ? id : null
}

function countOverlap(a: Set<string>, b: Set<string>): number {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a]
  let n = 0
  for (const id of small) if (large.has(id)) n++
  return n
}

export function deriveDestinationTree(input: {
  properties: DerivationProperty[]
  prefixes?: Partial<DestinationPrefixes>
  /** explorer-meta `taxonomies.cities`: labels for the city ids the rows
   *  carry, or the whole list when no row has a `city` key. See DestinationTree.cityTerms. */
  cityTerms?: DerivationTerm[]
  generatedAt?: string
}): DestinationTree {
  const properties = input.properties ?? []
  // Merge defaults per key, skipping non-strings: a Partial caller can pass an
  // explicitly-undefined key ({ island: stored ?? undefined }), and a plain
  // spread would let that undefined SHADOW the default and crash the trim
  // below. '' stays meaningful (level deliberately disabled).
  const prefixes: DestinationPrefixes = { ...DEFAULT_DESTINATION_PREFIXES }
  for (const level of Object.keys(DEFAULT_DESTINATION_PREFIXES) as DestinationLevel[]) {
    const supplied = input.prefixes?.[level]
    if (typeof supplied === 'string') prefixes[level] = supplied
  }
  const hygiene: DestinationHygieneRow[] = []

  /**
   * A derived node can only be STORED if it fits `destinationNodeSchema`:
   * positive integer id, non-blank label. Every source has to pass the same
   * gate, because the dashboard build enforces that contract by THROWING
   * (destination-tree-build.ts) — so one PMS term with a blank name or a
   * zero id would otherwise fail the operator's whole build with a message
   * that names nothing. Unusable terms are hygiene rows, like the empty
   * labels the prefix path already reported.
   *
   * `hygieneLabel` keeps the RAW name in the report where the display label
   * has been stripped to nothing ("(i) " reads better than "").
   */
  const usable = (
    level: DestinationHygieneRow['level'],
    id: number,
    label: string,
    count: number,
    hygieneLabel: string = label,
  ): boolean => {
    if (!Number.isInteger(id) || id <= 0) {
      hygiene.push({ label: hygieneLabel, level, issue: 'unusable-id', islands: [], count })
      return false
    }
    if (label === '') {
      hygiene.push({ label: hygieneLabel, level, issue: 'empty-label', islands: [], count })
      return false
    }
    return true
  }

  // Longest prefix first so overlapping prefixes resolve deterministically
  // ('(is)' must beat '(i)'), matching the PHP's uasort by strlen desc.
  //
  // The blank filter is a GUARD, not an operator-facing "disable this level":
  // `''.startsWith('')` is true for every name, so a blank prefix would
  // classify the entire tag vocabulary at that level. Production can't reach
  // it — the settings layer resolves a blank prefix back to the toolkit
  // default (config.ts) and so does the build pipeline's `storedPrefix` —
  // but a direct caller can, and this keeps that from silently exploding.
  const orderedPrefixes = (Object.entries(prefixes) as Array<[DestinationLevel, string]>)
    .filter(([, prefix]) => prefix.trim() !== '')
    .sort((a, b) => b[1].length - a[1].length)

  const classified: Classified[] = []
  for (const bucket of bucketTerms(properties, (p) => p.tags)) {
    const name = bucket.name.trim()
    const lowered = name.toLowerCase()
    for (const [level, prefix] of orderedPrefixes) {
      if (!lowered.startsWith(prefix.toLowerCase())) continue
      const label = name.slice(prefix.length).trim()
      if (!usable(level, bucket.id, label, bucket.ids.size, name)) break
      classified.push({ id: bucket.id, label, level, ids: bucket.ids })
      break
    }
  }

  const islands = classified
    .filter((c) => c.level === 'island')
    .sort((a, b) => compareDestinationNames(a.label, b.label))
    .map((c) => ({ id: c.id, label: c.label, count: c.ids.size, ids: c.ids, sides: [] as DestinationNode[] }))

  /**
   * Co-occurrence assignment: the island whose property set overlaps this
   * term's the most wins. Ties keep the first island (PHP uses a strict `>`),
   * and overlapping more than one island is a hygiene signal, not an error.
   */
  const assignIsland = (item: Classified, level: DestinationHygieneRow['level']): number | null => {
    let bestId: number | null = null
    let bestOverlap = 0
    const overlapped: number[] = []

    for (const island of islands) {
      const overlap = countOverlap(item.ids, island.ids)
      if (overlap > 0) overlapped.push(island.id)
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestId = island.id
      }
    }

    if (overlapped.length > 1) {
      hygiene.push({
        label: item.label,
        level,
        issue: 'spans-multiple-islands',
        islands: overlapped,
        count: item.ids.size,
      })
    }

    return bestId
  }

  for (const side of classified.filter((c) => c.level === 'side')) {
    const owner = assignIsland(side, 'side')
    if (owner === null) {
      // Sides without an island have no home in the UI — hygiene only.
      hygiene.push({
        label: side.label,
        level: 'side',
        issue: 'no-island-overlap',
        islands: [],
        count: side.ids.size,
      })
      continue
    }
    islands.find((i) => i.id === owner)!.sides.push({ id: side.id, label: side.label, count: side.ids.size })
  }

  /** Unlike sides, an unowned city/resort is KEPT and renders ungrouped. */
  const grouped = (items: Classified[], level: DestinationHygieneRow['level']): DestinationGrouped[] =>
    items
      .map((item) => ({
        id: item.id,
        label: item.label,
        count: item.ids.size,
        island: assignIsland(item, level),
      }))
      .sort((a, b) => compareDestinationNames(a.label, b.label))

  const cities = grouped(
    bucketTerms(properties, (p) => p.locations)
      .map((b) => ({
        id: b.id,
        label: b.name.trim(),
        level: 'island' as DestinationLevel,
        ids: b.ids,
      }))
      // Same gate the prefix path gets: a location term the PMS left unnamed
      // is a hygiene row, not a build-breaking row (see `usable`).
      .filter((b) => usable('city', b.id, b.label, b.ids.size)),
    'city',
  )

  const resorts = grouped(classified.filter((c) => c.level === 'resort'), 'resort')

  /**
   * City terms count their properties by the row's `city.id` alone (#1623).
   * No name matching: Central emits `city` as {id,name} or null, never a name
   * without an id, and filters `cities` by city_id — a property matched by
   * name would never come back from the pick it was counted under. A Set, not
   * a counter, so a property repeated across pages counts once.
   *
   * The payload's SHAPE decides the mode, not whether some id is usable: a
   * central#3137 row always has the key (null when the property has no
   * city), so a feed whose rows are all `city: null` has no city a pick could
   * return, and must not fall back to the meta list of dead picks. A meta
   * city is dropped only when EVERY row has told us its city: a row without
   * the key (a mixed payload) could be carrying it.
   */
  const keyed = properties.filter((p) => typeof p === 'object' && p !== null && 'city' in p).length
  const cityAware = keyed > 0
  const everyRowKeyed = keyed === properties.length
  const carried = new Map<number, { name: string; ids: Set<string> }>()
  for (const property of properties) {
    const id = cityTermId(property.city?.id)
    if (id === null) continue
    let bucket = carried.get(id)
    if (!bucket) {
      bucket = { name: '', ids: new Set() }
      carried.set(id, bucket)
    }
    const name = property.city?.name
    if (!bucket.name && typeof name === 'string') bucket.name = name.trim()
    bucket.ids.add(property.id)
  }

  // A blank label is reported by id: "(unnamed)" alone cannot tell the
  // operator which Central city to fix.
  const cityTermUsable = (t: DestinationGrouped) => usable('city-term', t.id, t.label, t.count, t.label || `#${t.id}`)
  let cityTerms: DestinationGrouped[]
  if (cityAware) {
    // explorer-meta keeps the label for an id it knows, so a rebuild renames
    // nothing; an id meta lacks is labelled from the row. Meta terms no
    // property carries are dropped (TreeBuilder's hide_empty + attach_ids):
    // picking one returns no properties.
    const metaName = new Map<number, string>()
    for (const t of input.cityTerms ?? []) {
      const id = cityTermId(t?.id)
      if (id !== null && !metaName.has(id)) metaName.set(id, String(t?.name ?? '').trim())
    }
    const counted = [...carried].map(([id, bucket]) => ({ id, label: metaName.get(id) || bucket.name, count: bucket.ids.size, island: null }))
    const unknown = everyRowKeyed
      ? []
      : [...metaName].filter(([id]) => !carried.has(id)).map(([id, label]) => ({ id, label, count: 0, island: null }))
    cityTerms = [...counted, ...unknown]
      .filter(cityTermUsable)
      .sort((a, b) => compareDestinationNames(a.label, b.label))
  } else {
    cityTerms = (input.cityTerms ?? [])
      // NaN, not Number(): an unsafe id must be hygiene, not a rounded row.
      .map((t) => ({ id: cityTermId(t?.id) ?? Number.NaN, label: String(t?.name ?? '').trim(), count: 0, island: null }))
      .filter(cityTermUsable)
      .sort((a, b) => compareDestinationNames(a.label, b.label))
  }

  const homes: DestinationHome[] = properties
    .map((p) => ({ label: String(p.title ?? '').trim(), slug: p.slug ?? '' }))
    .filter((h) => h.label !== '')
    .sort((a, b) => compareDestinationNames(a.label, b.label))

  return {
    v: DESTINATION_TREE_VERSION,
    islands: islands.map(({ ids: _ids, ...island }) => ({
      ...island,
      sides: island.sides.sort((a, b) => compareDestinationNames(a.label, b.label)),
    })),
    cities,
    cityTerms,
    resorts,
    homes,
    hygiene,
    generatedAt: input.generatedAt ?? new Date().toISOString(),
  }
}

/**
 * ── Storage contract ───────────────────────────────────────────────────────
 *
 * The tree is stored FLAT, as an array of node rows, for one structural
 * reason: the admin form engine materializes a blank value for every schema
 * key on EVERY widget save, and `getFinalSettings`' leaf merge lets a blank
 * widget value beat a real feed-global one (the #1064 Wave 2 `feeLines` bug —
 * see stripBlankFeeLines in src/lib/widget-settings-prep.ts).
 *
 * form-default-value-from-schema.ts decides the blank shape, and only two
 * kinds are safe: an optional NON-ENUM array and an optional scalar both
 * materialize to `undefined` (L57-58 / L27-36). An optional OBJECT keeps
 * unwrapping and materializes to `{}` (L60-63 -> L92-104), which is precisely
 * the shape that clobbers. A nested tree object would therefore need its own
 * stripBlank guard forever; a flat array cannot produce the bug at all.
 *
 * Two members of the derived tree are also NOT stored:
 *  - `homes` dominates the payload (measured on live feed 46: 16.6 KB of a
 *    23 KB tree, ~89 B per property) and widget settings ship to EVERY visitor
 *    on every page load. D2's search fetches property names lazily on first
 *    focus, which is what the spec asks for regardless.
 *  - `hygiene` is an operator diagnostic. The toolkit agrees: TreeBuilder
 *    writes it to its own option (HYGIENE_KEY), never into the cached tree.
 *
 * Measured stored sizes (synthetic, homes/hygiene excluded): 126 nodes 7.9 KB,
 * 380 nodes 23.2 KB, 940 nodes 57.7 KB. Real feed 46 (68 nodes) is 4.0 KB.
 */

export type StoredDestinationLevel = (typeof destinationNodeLevels)[number]
export { destinationNodeLevels }

export type StoredDestinationNode = {
  level: StoredDestinationLevel
  id: number
  label: string
  count: number
  /** Owning island term id. Absent for islands, unowned nodes, and city-terms. */
  parent?: number
}

/** Flatten a derived tree into the stored node rows. */
export function flattenTree(tree: DestinationTree): StoredDestinationNode[] {
  const rows: StoredDestinationNode[] = []

  for (const island of tree.islands) {
    rows.push({ level: 'island', id: island.id, label: island.label, count: island.count })
    for (const side of island.sides) {
      rows.push({ level: 'side', id: side.id, label: side.label, count: side.count, parent: island.id })
    }
  }

  const pushGrouped = (level: StoredDestinationLevel, nodes: DestinationGrouped[]) => {
    for (const node of nodes) {
      const row: StoredDestinationNode = { level, id: node.id, label: node.label, count: node.count }
      if (node.island !== null) row.parent = node.island
      rows.push(row)
    }
  }

  pushGrouped('city', tree.cities)
  pushGrouped('city-term', tree.cityTerms)
  pushGrouped('resort', tree.resorts)

  return rows
}

/**
 * Rebuild the renderable tree from stored rows. Unknown levels and side rows
 * whose island is missing are dropped rather than thrown on: stored settings
 * outlive the code that wrote them, and a half-readable tree must still render.
 */
export function unflattenTree(rows: StoredDestinationNode[] | undefined): Omit<DestinationTree, 'homes' | 'hygiene' | 'generatedAt' | 'v'> {
  const islands = new Map<number, DestinationIsland>()
  const cities: DestinationGrouped[] = []
  const cityTerms: DestinationGrouped[] = []
  const resorts: DestinationGrouped[] = []

  for (const row of rows ?? []) {
    if (row?.level === 'island') {
      islands.set(row.id, { id: row.id, label: row.label, count: row.count, sides: [] })
    }
  }

  for (const row of rows ?? []) {
    if (!row) continue
    const grouped: DestinationGrouped = {
      id: row.id,
      label: row.label,
      count: row.count,
      island: row.parent ?? null,
    }
    switch (row.level) {
      case 'side':
        islands.get(row.parent ?? -1)?.sides.push({ id: row.id, label: row.label, count: row.count })
        break
      case 'city':
        cities.push(grouped)
        break
      case 'city-term':
        cityTerms.push({ ...grouped, island: null })
        break
      case 'resort':
        resorts.push(grouped)
        break
      default:
        break
    }
  }

  return { islands: [...islands.values()], cities, cityTerms, resorts }
}
